import type { CostRow } from '@trivio/contracts';
import { providerIdPt } from '../../utils/enums-ptbr';
import { formatCentsToBRL } from '../../utils/money';

const CSV_HEADER = ['Período', 'Provedor', 'Transportadora', 'Nº de etiquetas', 'Valor total'];

/** Wraps a field in double quotes and escapes embedded quotes only when needed — keeps plain values (the common case here) readable in the raw file. */
function csvField(value: string): string {
  return /[",\n]/u.test(value) ? `"${value.replace(/"/gu, '""')}"` : value;
}

// UTF-8 BOM, built via `fromCharCode` (never a raw/escaped byte pasted into
// source, which editors and diffs render as invisible and easy to corrupt)
// so Excel on Windows — the realistic target for a lojista — opens the
// accented pt-BR headers/currency as UTF-8 instead of guessing a legacy
// codepage and mangling them.
const UTF8_BOM = String.fromCharCode(0xfeff);

/**
 * Builds the CSV body straight from the rows already rendered in the table
 * (CLAUDE.md/issue: no separate export endpoint — the client never
 * recomputes or re-fetches anything to export, it just re-serializes what's
 * on screen).
 */
export function costRowsToCsv(rows: CostRow[]): string {
  const lines = [CSV_HEADER.map(csvField).join(',')];
  for (const row of rows) {
    lines.push(
      [
        row.period,
        row.provider ? providerIdPt(row.provider) : '',
        row.carrier ?? '',
        String(row.label_count),
        formatCentsToBRL(row.total_price_cents),
      ]
        .map(csvField)
        .join(','),
    );
  }
  return `${UTF8_BOM}${lines.join('\r\n')}`;
}

/** Triggers a normal browser download — same `URL.createObjectURL` + throwaway `<a download>` pattern already used for label PDFs (`LabelsPage.tsx`/`LabelDetailPage.tsx`), just with a `text/csv` blob instead of a PDF one. */
export function downloadCostsCsv(rows: CostRow[], filename: string): void {
  const blob = new Blob([costRowsToCsv(rows)], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const anchor = window.document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  window.document.body.appendChild(anchor);
  anchor.click();
  window.document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
}
